import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const COLORS = {
  background: '#FAF7F2',
  labels: '#4A4A4A',
  shadows: '#8B857A',
  romantic: '#E6B3C2',
  borders: '#C5BBB0',
};

export default function PrivacyPolicy() {
  return (
    <div className="privacy-container" style={{ backgroundColor: COLORS.background, minHeight: '100vh', padding: '2rem' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-block mb-4 text-sm hover:underline"
            style={{ color: COLORS.shadows }}
          >
            ← Back to Map
          </Link>
          <h1 className="text-4xl font-bold mb-2" style={{ color: COLORS.labels }}>
            Privacy Policy
          </h1>
          <p className="text-sm" style={{ color: COLORS.shadows }}>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="privacy-content" style={{ color: COLORS.labels, lineHeight: '1.8' }}>
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>1. Introduction</h2>
            <p className="mb-4">
              Welcome to Velvynote ("we," "our," or "us"). We are committed to protecting your privacy and being 
              transparent about how we collect, use, and share your information.
            </p>
            <p>
              This Privacy Policy explains our practices regarding the collection, use, and disclosure of information 
              when you use our location-based memory sharing service. By using Velvynote, you agree to the collection 
              and use of information in accordance with this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>2.1 Information You Provide</h3>
            <p className="mb-2">When you use Velvynote, we may collect:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Account Information:</strong> If you create an account, we collect your email address and 
                  basic profile information provided by your authentication provider (e.g., Google).</li>
              <li><strong>Memory Content:</strong> The text content, color, and other metadata of memories you create.</li>
              <li><strong>Location Data:</strong> Geographic coordinates (latitude and longitude) for each memory you post.</li>
              <li><strong>Communications:</strong> Any messages or feedback you send to us.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>2.2 Automatically Collected Information</h3>
            <p className="mb-2">We automatically collect certain information when you use the Service:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Usage Data:</strong> Information about how you interact with the Service, including pages 
                  viewed, features used, and time spent.</li>
              <li><strong>Device Information:</strong> Device type, operating system, browser type, and unique device 
                  identifiers.</li>
              <li><strong>Log Data:</strong> IP address, access times, error logs, and other diagnostic information.</li>
              <li><strong>Map Interaction:</strong> Map viewport boundaries, zoom levels, and navigation patterns.</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>2.3 Cookies and Tracking Technologies</h3>
            <p className="mb-4">
              We use cookies and similar tracking technologies to maintain your session, remember your preferences, 
              and analyze Service usage. You can control cookies through your browser settings, but disabling cookies 
              may affect Service functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>3. How We Use Your Information</h2>
            <p className="mb-2">We use the collected information for the following purposes:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Service Operation:</strong> To provide, maintain, and improve the Service</li>
              <li><strong>Memory Display:</strong> To display your memories and other users' memories on the map</li>
              <li><strong>User Experience:</strong> To personalize your experience and remember your preferences</li>
              <li><strong>Communication:</strong> To respond to your inquiries and send Service-related notifications</li>
              <li><strong>Security:</strong> To detect, prevent, and address security issues and unauthorized access</li>
              <li><strong>Analytics:</strong> To understand usage patterns and improve our Service</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our Terms of Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>4. How We Share Your Information</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>4.1 Public Information</h3>
            <p className="mb-4">
              Memories you create are publicly visible on the map. This includes the memory text, color, location 
              coordinates, creation timestamp, and whether the memory is anonymous. We display memories through vector 
              tiles that are cached and served via content delivery networks.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>4.2 Service Providers</h3>
            <p className="mb-4">
              We may share your information with third-party service providers who help us operate the Service, including:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Hosting Providers:</strong> For data storage and server infrastructure (e.g., Firebase, Supabase)</li>
              <li><strong>CDN Providers:</strong> For efficient content delivery (e.g., Cloudflare R2)</li>
              <li><strong>Authentication Providers:</strong> For user authentication (e.g., Google Sign-In)</li>
              <li><strong>Analytics Services:</strong> For usage analytics and performance monitoring</li>
            </ul>
            <p className="mb-4">
              These service providers are bound by contractual obligations to protect your information and use it only 
              for the purposes we specify.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>4.3 Legal Requirements</h3>
            <p className="mb-4">
              We may disclose your information if required by law, legal process, or governmental request, or to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Comply with legal obligations</li>
              <li>Protect and defend our rights or property</li>
              <li>Prevent or investigate possible wrongdoing</li>
              <li>Protect the personal safety of users or the public</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>4.4 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the 
              acquiring entity. We will notify you of any such change in ownership or control.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>5. Data Storage and Security</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>5.1 Data Storage</h3>
            <p className="mb-4">
              Your data is stored on secure servers provided by our hosting partners. Memories are stored in cloud 
              databases (Supabase), and map tiles are stored and served via content delivery networks 
              (Cloudflare R2) for efficient global access.
            </p>
            <p className="mb-4">
              Data may be stored and processed in countries other than your country of residence. By using the Service, 
              you consent to the transfer of your information to these countries.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>5.2 Security Measures</h3>
            <p className="mb-2">We implement appropriate technical and organizational measures to protect your information:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security audits and updates</li>
              <li>Access logging and monitoring</li>
            </ul>
            <p className="mb-4">
              However, no method of transmission over the internet or electronic storage is 100% secure. While we 
              strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>6. Your Rights and Choices</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>6.1 Access and Correction</h3>
            <p className="mb-4">
              You can access and update your account information through the Service settings. You can also view and 
              delete your memories directly from the map interface.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>6.2 Data Deletion</h3>
            <p className="mb-4">
              You can delete your memories at any time. Deleted memories will be removed from the Service, though 
              cached versions in CDNs may persist for a short period. If you delete your account, we will delete your 
              personal information subject to our data retention policies and legal obligations.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>6.3 Anonymous Use</h3>
            <p className="mb-4">
              You can use the Service without creating an account. Anonymous memories are linked to temporary identifiers 
              and may have limited functionality (e.g., you may not be able to delete them later).
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>6.4 Opt-Out Rights</h3>
            <p className="mb-4">
              You can opt out of non-essential communications from us. However, we may still send you important 
              Service-related notifications.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>6.5 GDPR and CCPA Rights</h3>
            <p className="mb-2">If you are located in the European Economic Area (EEA) or California, you have additional rights:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Right to Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Right to Object:</strong> Object to processing of your personal data</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of how we process your data</li>
            </ul>
            <p>
              To exercise these rights, please contact us at <a href="mailto:hello@velvynote.com" style={{ color: COLORS.romantic }}>hello@velvynote.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>7. Children's Privacy</h2>
            <p className="mb-4">
              Velvynote is not intended for children under the age of 13 (or the minimum age in your jurisdiction). 
              We do not knowingly collect personal information from children under 13.
            </p>
            <p>
              If we become aware that we have collected personal information from a child under 13, we will take steps 
              to delete such information. If you believe we have collected information from a child under 13, please 
              contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>8. Data Retention</h2>
            <p className="mb-4">
              We retain your information for as long as necessary to provide the Service and fulfill the purposes 
              described in this Privacy Policy, unless a longer retention period is required or permitted by law.
            </p>
            <p>
              When you delete a memory or account, we will delete the associated data within a reasonable timeframe, 
              subject to technical limitations (e.g., cached CDN content) and legal obligations (e.g., retention 
              requirements for financial records).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>9. Third-Party Links and Services</h2>
            <p className="mb-4">
              The Service may contain links to third-party websites or services. We are not responsible for the privacy 
              practices of these third parties. We encourage you to review their privacy policies before providing any 
              information to them.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>10. Changes to This Privacy Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by 
              posting the new policy on this page and updating the "Last updated" date.
            </p>
            <p>
              Your continued use of the Service after changes become effective constitutes acceptance of the updated 
              Privacy Policy. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>11. International Data Transfers</h2>
            <p className="mb-4">
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have different data protection laws than your country.
            </p>
            <p>
              By using the Service, you consent to the transfer of your information to these countries. We take 
              appropriate measures to ensure your information is protected in accordance with this Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>12. Contact Us</h2>
            <p className="mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us at:
            </p>
            <p className="mb-4">
              <strong>Email:</strong> <a href="mailto:hello@velvynote.com" style={{ color: COLORS.romantic }}>hello@velvynote.com</a>
            </p>
            <p>
              We will respond to your inquiries within a reasonable timeframe.
            </p>
          </section>

          <section className="mb-8 pt-8 border-t" style={{ borderColor: COLORS.borders }}>
            <p className="text-sm italic" style={{ color: COLORS.shadows }}>
              By using Velvynote, you acknowledge that you have read and understood this Privacy Policy and agree 
              to the collection and use of your information as described herein.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

